import { useRef, useState, useEffect } from "react";
// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";

// project import

import MainCard from "components/MainCard";
import Transitions from "components/@extended/Transitions";

import { ThemeMode } from "config";

import { useGetMenu } from "api/menu";
import {
  Icon,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import ReadOutlined from "@ant-design/icons/ReadOutlined";
import useAuth from "hooks/useAuth";

export type ICommunity = { id: number; name: string };

type SelectCommunityProps = {
  setCommunity: (community: ICommunity) => void;
};

export default function SelectCommunity({
  setCommunity,
}: SelectCommunityProps) {
  const {
    communities,
    menuLoading,
    selectedCommunity: selectedCommunity,
  } = useGetMenu();
  const { switchLogin } = useAuth();
  const theme = useTheme();

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const onSelectCommunity = (community: ICommunity) => {
    if (community.id != selectedCommunity) {
      switchLogin(community?.id).then(() => {
        window.location.reload();
      });
    }
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!menuLoading) {
      setCommunity(
        communities.find(
          (communities: { id: number; name: string }) =>
            communities.id == selectedCommunity
        )
      );
    }
  }, [menuLoading, selectedCommunity]);

  useEffect(() => {}, []);

  const iconBackColorOpen =
    theme.palette.mode === ThemeMode.DARK ? "background.default" : "grey.100";

  return (
    <>
      {communities != null && communities.length > 1 ? (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
          <Tooltip title={"Communities"}>
            <ButtonBase
              sx={{
                p: 0.25,
                bgcolor: open ? iconBackColorOpen : "transparent",
                borderRadius: 1,
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === ThemeMode.DARK
                      ? "secondary.light"
                      : "secondary.lighter",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.secondary.dark}`,
                  outlineOffset: 2,
                },
              }}
              aria-label="open profile"
              ref={anchorRef}
              aria-controls={open ? "profile-grow" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ p: 0.5 }}
              >
                {menuLoading ? (
                  "Loading"
                ) : (
                  <Icon>
                    <ReadOutlined />
                  </Icon>
                )}
              </Stack>
            </ButtonBase>
          </Tooltip>
          <Popper
            //   placement="bottom-start"
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            popperOptions={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 9],
                  },
                },
              ],
            }}
          >
            {({ TransitionProps }) => (
              <Transitions
                type="grow"
                position="top-right"
                in={open}
                {...TransitionProps}
              >
                <Paper
                  sx={{
                    boxShadow: theme.customShadows.z1,
                    width: 290,
                    minWidth: 240,
                    maxWidth: { xs: 250, md: 290 },
                  }}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <MainCard elevation={0} border={false} content={false}>
                      {menuLoading ? (
                        <></>
                      ) : (
                        <Stack>
                          <List
                            component="nav"
                            sx={{
                              p: 0,
                              "& .MuiListItemIcon-root": { minWidth: 32 },
                            }}
                          >
                            {communities.map(
                              (community: ICommunity, idx: number) => {
                                return (
                                  <ListItemButton
                                    key={idx}
                                    sx={{
                                      backgroundColor:
                                        selectedCommunity != null &&
                                        community?.id == selectedCommunity
                                          ? theme.palette.primary.lighter
                                          : "white",
                                      color:
                                        selectedCommunity != null &&
                                        community?.id == selectedCommunity
                                          ? theme.palette.primary.dark
                                          : "black",
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.primary.lighter,
                                        color: theme.palette.primary.dark,
                                      },
                                    }}
                                  >
                                    <ListItemText
                                      primary={community.name}
                                      onClick={() => {
                                        onSelectCommunity(community);
                                      }}
                                    />
                                  </ListItemButton>
                                );
                              }
                            )}
                          </List>
                        </Stack>
                      )}
                    </MainCard>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </Popper>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
}
